import { useCallback, useMemo, useState, useEffect, useRef } from 'react';

import { ContentNugget, transformSearchResultsToNuggets } from '@backrs_ui/common-ui';

import {
  CareerPathSearchCategory,
  CareerPathSearchParentCategory,
} from '@backrs_ui/grow-ui/src/models/models';
import { useSearchCareerPathContentQuery } from '@backrs_ui/grow-ui/src/redux/careerPath/careerPath.api';
import { setSearchParamsForSection } from '@backrs_ui/grow-ui/src/redux/careerPath/careerPathSearch';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { useLazyLoadResults } from './useLazyLoadResults';
import { CAREER_PATH_CONSTANTS } from '../constants';

export const useResourceNuggetSectionSearch = (
  careerPathName: string,
  category: CareerPathSearchParentCategory,
) => {
  const dispatch = useAppDispatch();
  const hasStoredInitialCount = useRef(false);

  const searchState = useAppSelector((state) => state.careerPathSearch);
  const { careerCategories } = useAppSelector((state) => state.careerPath);

  const searchParams = useMemo(() => {
    return (
      searchState.sections[category.name] || {
        searchTerm: '',
        selectedFilters: [],
        searchResults: undefined,
        hasInitialSearchBeenMade: false,
      }
    );
  }, [searchState.sections, category.name]);

  const selectedFilters = useMemo(
    () => searchParams?.selectedFilters || [],
    [searchParams?.selectedFilters],
  );

  const [initialResults, setInitialResults] = useState<ContentNugget[]>([]);

  const { lazyLoadState, loadMoreResults, resetLazyLoadState } = useLazyLoadResults(
    careerPathName,
    selectedFilters,
  );

  // Get default filters for this category
  const defaultCategoryFilters = useMemo(() => {
    return category.categories.map((subCategory) => subCategory.key);
  }, [category.categories]);

  // Use RTK Query hook
  const { data: searchResults, isLoading: isSearching } = useSearchCareerPathContentQuery(
    {
      search_term: careerPathName,
      page: CAREER_PATH_CONSTANTS.LAZY_LOAD.INITIAL_PAGE,
      perPage: CAREER_PATH_CONSTANTS.LAZY_LOAD.RESULTS_PER_PAGE,
      types: selectedFilters.length > 0 ? selectedFilters : defaultCategoryFilters,
      sectionName: category.name,
    },
    {
      skip: !searchParams.hasInitialSearchBeenMade,
    },
  );

  // Initialize section with category-specific filters
  useEffect(() => {
    if (!searchParams.hasInitialSearchBeenMade) {
      dispatch(
        setSearchParamsForSection({
          sectionName: category.name,
          searchParams: {
            searchTerm: careerPathName,
            selectedFilters: defaultCategoryFilters,
            searchResults: undefined,
            hasInitialSearchBeenMade: true,
          },
        }),
      );
    }
  }, [
    dispatch,
    category.name,
    careerPathName,
    defaultCategoryFilters,
    searchParams.hasInitialSearchBeenMade,
  ]);

  useEffect(() => {
    if (searchResults) {
      const initialNuggets = transformSearchResultsToNuggets(searchResults.results).nuggets;

      setInitialResults(initialNuggets);

      // Store the initial result count only on the very first search
      if (!hasStoredInitialCount.current) {
        hasStoredInitialCount.current = true;
        dispatch(
          setSearchParamsForSection({
            sectionName: category.name,
            searchParams: {
              ...searchParams,
              initialResultCount: searchResults.total,
            },
          }),
        );
      }
    }
  }, [searchResults, dispatch, category.name, searchParams]);

  const handleSettingSelectedItems = useCallback(
    (items: CareerPathSearchCategory[]) => {
      // Prevent complete deselection
      if (items.length === 0) {
        return;
      }

      resetLazyLoadState();

      dispatch(
        setSearchParamsForSection({
          sectionName: category.name,
          searchParams: {
            ...searchParams,
            selectedFilters: items,
          },
        }),
      );
    },
    [dispatch, searchParams, resetLazyLoadState, category.name],
  );

  const combinedResults = useMemo(() => {
    return [...initialResults, ...lazyLoadState.allResults].filter(
      (result, index, self) => index === self.findIndex((t) => t.id === result.id),
    );
  }, [initialResults, lazyLoadState.allResults]);

  return {
    currentSearchTerm: careerPathName,
    selectedFilters,
    searchResults,
    isSearching,
    handleSettingSelectedItems,
    loadMoreResults,
    combinedResults,
    hasMore: lazyLoadState.hasMore,
    isLoadingMore: lazyLoadState.isLoadingMore,
    careerCategories,
  };
};
